import { Button, Spinner } from 'components/Elements';
import { Notifications } from 'components/Notifications/Notifications';
import { AuthProvider } from 'lib/auth';
import { CMSProvider } from 'lib/cms';
import { queryClient } from 'lib/react-query';
import * as React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { BrowserRouter as Router } from 'react-router-dom';
import { motion } from 'framer-motion';

const ErrorFallback = () => {
  return (
    <div
      role="alert"
      aria-labelledby="error-heading"
      aria-describedby="error-description"
      className="w-screen h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100 px-4"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.94, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        className="
          bg-white
          rounded-3xl
          shadow-lg
          border border-slate-200
          max-w-lg
          w-full
          p-10
          text-center
        "
      >
        {/* Icon */}
        <motion.div
          initial={{ rotate: -2 }}
          animate={{ rotate: 0 }}
          className="mx-auto mb-5 flex items-center justify-center h-16 w-16
                     rounded-2xl bg-red-50 border border-red-100"
        >
          <svg
            className="h-9 w-9 text-red-500"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3m0 4h.01M4.93 4.93a10 10 0 1114.14 14.14A10 10 0 014.93 4.93z"
            />
          </svg>
        </motion.div>

        {/* Heading */}
        <h2 id="error-heading" className="text-2xl font-semibold text-slate-800">
          Something went wrong
        </h2>
        <p id="error-description" className="text-slate-600 mt-3 leading-relaxed">
          We’re experiencing an issue loading your workspace. Try refreshing the page or clearing
          your browser data.
        </p>

        {/* Refresh Button */}
        <motion.div whileHover={{ scale: 1.02 }} className="mt-7">
          <Button
            onClick={() => {
              localStorage.clear();
              window.location.assign(window.location.origin);
            }}
          >
            Refresh Page
          </Button>
        </motion.div>

        {/* Help */}
        <p className="text-sm text-slate-500 mt-6">
          Need help? Contact{' '}
          <a href="mailto:ashitacreatives@gmail.com" className="text-[#AE3F1E] underline">
            ashitacreatives@gmail.com
          </a>
        </p>
      </motion.div>
    </div>
  );
};

export const AppProvider = ({ children }) => {
  return (
    <React.Suspense
      fallback={
        <div className="flex items-center justify-center w-screen h-screen">
          <Spinner size="xl" />
        </div>
      }
    >
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <HelmetProvider>
          <QueryClientProvider client={queryClient}>
            {process.env.NODE_ENV !== 'test' && <ReactQueryDevtools />}
            <Notifications />
            <AuthProvider>
              <CMSProvider>
                <Router>{children}</Router>
              </CMSProvider>
            </AuthProvider>
          </QueryClientProvider>
        </HelmetProvider>
      </ErrorBoundary>
    </React.Suspense>
  );
};
